import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import api from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
    FileBarChart, RefreshCw, Loader2, ArrowDown, ArrowUp, Minus,
    AlertTriangle, AlertOctagon, TrendingDown, TrendingUp, AlertCircle,
    Users, Eye, UserPlus, LogIn, School, Compass, BookOpen, Sparkles,
} from 'lucide-react';

const STAGE_ICONS = {
    visita: Eye,
    crear_cuenta: UserPlus,
    registro_completado: Users,
    primera_sesion: LogIn,
    establecimiento: School,
    estrategia: Compass,
    modulos: BookOpen,
};

function DeltaPill({ curr, prev, suffix = '' }) {
    const c = curr || 0;
    const p = prev || 0;
    if (p === 0 && c === 0) {
        return <span className="text-[10px] text-slate-400">—</span>;
    }
    const diff = c - p;
    const pct = p > 0 ? Math.round((diff / p) * 100) : null;
    const kind = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';
    return (
        <span
            className={cn(
                'inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] font-semibold',
                kind === 'up' && 'bg-emerald-50 text-emerald-700',
                kind === 'down' && 'bg-rose-50 text-rose-700',
                kind === 'flat' && 'bg-slate-100 text-slate-500'
            )}
            title={`Ayer: ${p}`}
        >
            {kind === 'up' && <ArrowUp className="h-3 w-3" />}
            {kind === 'down' && <ArrowDown className="h-3 w-3" />}
            {kind === 'flat' && <Minus className="h-3 w-3" />}
            {pct != null ? `${diff > 0 ? '+' : ''}${pct}%` : `+${c}`} {suffix}
        </span>
    );
}

function AlertIcon({ icon }) {
    if (icon === 'trending-down') return <TrendingDown className="h-4 w-4" />;
    if (icon === 'alert-octagon') return <AlertOctagon className="h-4 w-4" />;
    return <AlertTriangle className="h-4 w-4" />;
}

// ─────────────────────────────────────────────────────────────────────────────
// INFORME DIARIO
// ─────────────────────────────────────────────────────────────────────────────
function DailyReport({ data, onRefresh, loading }) {
    if (!data) return null;
    const todayLabel = new Date(`${data.today_date}T12:00:00`).toLocaleDateString('es-CL', {
        weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
    });
    const today = data.actividad.today;
    const yesterday = data.actividad.yesterday;
    const labels = data.actividad.labels;
    const stages = ['visita', 'crear_cuenta', 'registro_completado', 'primera_sesion', 'establecimiento', 'estrategia', 'modulos'];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <p className="font-heading text-sm font-bold text-slate-900 capitalize">{todayLabel}</p>
                    <p className="text-xs text-slate-500">Datos en hora Chile · Comparado vs. ayer</p>
                </div>
                <Button size="sm" variant="outline" onClick={onRefresh} disabled={loading} className="gap-1.5" data-testid="report-daily-refresh">
                    <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
                    Refrescar
                </Button>
            </div>

            {/* Alertas */}
            {data.alerts.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-2" data-testid="daily-alerts">
                    <p className="font-heading text-sm font-bold text-amber-900 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        Alertas del día ({data.alerts.length})
                    </p>
                    <ul className="space-y-1.5">
                        {data.alerts.map((a, i) => (
                            <li key={i} className={cn(
                                'flex items-start gap-2 text-xs',
                                a.level === 'error' ? 'text-rose-700' : 'text-amber-800'
                            )}>
                                <AlertIcon icon={a.icon} />
                                <div>
                                    <strong>{a.title}.</strong> <span className="text-slate-600">{a.detail}</span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Actividad del día */}
            <div>
                <p className="font-heading text-sm font-bold text-slate-900 mb-2">Actividad</p>
                <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 lg:grid-cols-7" data-testid="daily-activity">
                    {stages.map((k) => {
                        const Icon = STAGE_ICONS[k];
                        return (
                            <div
                                key={k}
                                className="rounded-lg border border-slate-200 bg-white p-3"
                                data-testid={`daily-stage-${k}`}
                            >
                                <div className="flex items-center justify-between mb-1.5">
                                    <Icon className="h-3.5 w-3.5 text-slate-500" />
                                    <DeltaPill curr={today[k]} prev={yesterday[k]} />
                                </div>
                                <p className="text-[10px] text-slate-500 leading-tight">{labels[k]}</p>
                                <p className="font-mono text-lg font-bold text-slate-900">{today[k]}</p>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Conversiones */}
            <div>
                <p className="font-heading text-sm font-bold text-slate-900 mb-2">Conversiones</p>
                <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                    <table className="w-full text-xs" data-testid="daily-conversions">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50 text-left">
                                <th className="px-3 py-2 font-semibold text-slate-600">Conversión</th>
                                <th className="px-3 py-2 font-semibold text-slate-600 text-right">Hoy</th>
                                <th className="px-3 py-2 font-semibold text-slate-600 text-right">Ayer</th>
                                <th className="px-3 py-2 font-semibold text-slate-600 text-right">Δ p.p.</th>
                                <th className="px-3 py-2 font-semibold text-slate-600 text-right">Base hoy</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.conversions.map((c, i) => (
                                <tr key={i} className="border-b border-slate-100 last:border-0">
                                    <td className="px-3 py-2 text-slate-700 font-medium">{c.label}</td>
                                    <td className="px-3 py-2 text-right font-mono">
                                        {c.today_pct != null ? <span className="text-slate-900 font-semibold">{c.today_pct}%</span> : <span className="text-slate-300">—</span>}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono text-slate-600">
                                        {c.yesterday_pct != null ? `${c.yesterday_pct}%` : '—'}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono">
                                        {c.delta_pp != null ? (
                                            <span className={cn(
                                                c.delta_pp > 0 ? 'text-emerald-700' : c.delta_pp < 0 ? 'text-rose-700' : 'text-slate-400'
                                            )}>
                                                {c.delta_pp > 0 ? '+' : ''}{c.delta_pp}
                                            </span>
                                        ) : <span className="text-slate-300">—</span>}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono text-slate-500 text-[10px]">
                                        {c.today_to}/{c.today_from}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Usuarios perdidos */}
            <div>
                <p className="font-heading text-sm font-bold text-slate-900 mb-2">Usuarios perdidos por etapa</p>
                <div className="grid gap-2 grid-cols-2 lg:grid-cols-4" data-testid="daily-lost">
                    {data.lost_users.map((l) => (
                        <div key={l.stage} className="rounded-lg border border-rose-100 bg-rose-50/40 p-3">
                            <p className="text-[10px] text-rose-700 font-semibold uppercase tracking-wide">{l.label}</p>
                            <div className="flex items-baseline justify-between mt-1">
                                <p className="font-mono text-2xl font-bold text-rose-900">{l.today}</p>
                                <span className={cn(
                                    'text-[10px] font-medium',
                                    l.delta > 0 ? 'text-rose-700' : l.delta < 0 ? 'text-emerald-700' : 'text-slate-500'
                                )}>
                                    {l.delta > 0 ? '+' : ''}{l.delta} vs. ayer
                                </span>
                            </div>
                            <p className="text-[10px] text-slate-500 mt-1">Ayer: {l.yesterday}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Errores técnicos */}
            {data.errors && data.errors.length > 0 && (
                <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-4" data-testid="daily-errors">
                    <p className="font-heading text-sm font-bold text-rose-900 mb-2 flex items-center gap-1.5">
                        <AlertOctagon className="h-4 w-4" />
                        Errores técnicos detectados ({data.errors.length})
                    </p>
                    <ul className="space-y-1 max-h-40 overflow-y-auto">
                        {data.errors.map((er, i) => (
                            <li key={i} className="text-xs text-rose-800">
                                <code className="bg-white px-1.5 py-0.5 rounded text-[10px]">{er.stage}</code>
                                {' '}{er.error}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// INFORME SEMANAL
// ─────────────────────────────────────────────────────────────────────────────
function TrendCard({ label, current, previous, trend, pct }) {
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
            <div className="flex items-baseline justify-between mt-1">
                <p className="font-mono text-2xl font-bold text-slate-900">{current}</p>
                <span className={cn(
                    'inline-flex items-center gap-0.5 text-xs font-semibold',
                    trend === 'up' && 'text-emerald-700',
                    trend === 'down' && 'text-rose-700',
                    trend === 'flat' && 'text-slate-500'
                )}>
                    {trend === 'up' && <TrendingUp className="h-3.5 w-3.5" />}
                    {trend === 'down' && <TrendingDown className="h-3.5 w-3.5" />}
                    {trend === 'flat' && <Minus className="h-3.5 w-3.5" />}
                    {pct != null ? `${pct > 0 ? '+' : ''}${pct}%` : 'nuevo'}
                </span>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Semana anterior: {previous}</p>
        </div>
    );
}

function WeeklyReport({ data, onRefresh, loading }) {
    if (!data) return null;
    const stages = data.funnel_table;
    const period = `${data.period_current.start} → ${data.period_current.end}`;
    const prevPeriod = `${data.period_previous.start} → ${data.period_previous.end}`;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <p className="font-heading text-sm font-bold text-slate-900">Semana actual: {period}</p>
                    <p className="text-xs text-slate-500">vs. semana anterior: {prevPeriod}</p>
                </div>
                <Button size="sm" variant="outline" onClick={onRefresh} disabled={loading} className="gap-1.5" data-testid="report-weekly-refresh">
                    <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
                    Refrescar
                </Button>
            </div>

            {/* Resumen ejecutivo */}
            <div className="rounded-xl border-2 border-slate-900/10 bg-gradient-to-br from-slate-50 to-amber-50/40 p-5" data-testid="weekly-summary">
                <p className="font-heading text-sm font-bold text-slate-900 flex items-center gap-2 mb-2">
                    <Sparkles className="h-4 w-4 text-amber-600" />
                    Resumen ejecutivo
                </p>
                <p className="text-sm text-slate-700 leading-relaxed">{data.resumen_ejecutivo}</p>
            </div>

            {/* Tendencias */}
            <div>
                <p className="font-heading text-sm font-bold text-slate-900 mb-2">Tendencias</p>
                <div className="grid gap-3 sm:grid-cols-3" data-testid="weekly-trends">
                    <TrendCard label="Visitas" {...data.tendencias.visitas} />
                    <TrendCard label="Registros" {...data.tendencias.registros} />
                    <TrendCard label="Usuarios activos" {...data.tendencias.usuarios_activos} />
                </div>
            </div>

            {/* Embudo */}
            <div>
                <p className="font-heading text-sm font-bold text-slate-900 mb-2">Embudo semanal</p>
                <div className="space-y-1.5" data-testid="weekly-funnel">
                    {stages.map((s, i) => {
                        const Icon = STAGE_ICONS[s.stage];
                        const widthPct = stages[0].count > 0 ? Math.max(8, (s.count / stages[0].count) * 100) : 8;
                        const isBiggestDrop = data.biggest_drop && data.biggest_drop.to_stage === s.stage;
                        return (
                            <div key={s.stage} className={cn(
                                'rounded-lg border bg-white p-3 transition-colors',
                                isBiggestDrop ? 'border-rose-300 bg-rose-50/50' : 'border-slate-200'
                            )}>
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <Icon className="h-4 w-4 text-slate-500 shrink-0" />
                                        <span className="text-sm font-medium text-slate-900">{s.label}</span>
                                        {isBiggestDrop && (
                                            <Badge className="bg-rose-500 text-white text-[9px] px-1.5 py-0">Mayor fuga</Badge>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0">
                                        <span className="font-mono text-sm font-bold text-slate-900">{s.count}</span>
                                        {i > 0 && s.conv_vs_prev != null && (
                                            <span className="text-[10px] text-slate-500 font-mono w-12 text-right">
                                                {s.conv_vs_prev}%
                                            </span>
                                        )}
                                        {i > 0 && s.drop_pct != null && (
                                            <span className={cn(
                                                'text-[10px] font-mono w-14 text-right',
                                                s.drop_pct > 50 ? 'text-rose-700' : 'text-slate-400'
                                            )}>
                                                -{s.drop_pct}%
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {/* Barra de progreso */}
                                <div className="mt-2 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                    <div
                                        className={cn(
                                            'h-full rounded-full transition-all',
                                            isBiggestDrop ? 'bg-rose-400' : 'bg-slate-700'
                                        )}
                                        style={{ width: `${widthPct}%` }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
                <p className="text-[10px] text-slate-400 mt-2">% Conversión vs. etapa anterior · % Caída desde etapa anterior</p>
            </div>

            {/* Punto de fuga principal */}
            {data.biggest_drop && (
                <div className="rounded-xl border border-rose-200 bg-rose-50/60 p-4" data-testid="weekly-biggest-drop">
                    <p className="font-heading text-sm font-bold text-rose-900 flex items-center gap-1.5">
                        <TrendingDown className="h-4 w-4" />
                        Principal punto de fuga
                    </p>
                    <p className="mt-1 text-sm text-rose-800">
                        Mayor caída entre <strong>{data.biggest_drop.from_label}</strong> y <strong>{data.biggest_drop.to_label}</strong>:{' '}
                        <span className="font-mono">{data.biggest_drop.drop_pct}%</span> de abandono
                        {' '}({data.biggest_drop.lost_count} docentes perdidos).
                    </p>
                </div>
            )}

            {/* Lost breakdown + detail */}
            <div className="grid gap-6 lg:grid-cols-2">
                <div>
                    <p className="font-heading text-sm font-bold text-slate-900 mb-2">Usuarios perdidos por etapa</p>
                    <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="weekly-lost-breakdown">
                        {data.lost_breakdown.map((l) => (
                            <div key={l.stage} className="flex items-center justify-between px-3 py-2">
                                <span className="text-sm text-slate-700">{l.label}</span>
                                <span className="font-mono text-sm font-bold text-rose-700">{l.count}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <p className="font-heading text-sm font-bold text-slate-900 mb-2">Detalle (top 20 más recientes)</p>
                    <div className="rounded-xl border border-slate-200 bg-white max-h-72 overflow-y-auto" data-testid="weekly-lost-detail">
                        {data.lost_users_detail.length === 0 ? (
                            <p className="text-xs text-slate-400 text-center py-6">Sin abandonos esta semana ✨</p>
                        ) : (
                            <ul className="divide-y divide-slate-100">
                                {data.lost_users_detail.map((u, i) => (
                                    <li key={i} className="px-3 py-2 text-xs">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                                <p className="font-medium text-slate-900 truncate">
                                                    {u.nombre || u.email || u.user_id?.slice(0, 8)}
                                                </p>
                                                {u.email && <p className="text-[10px] text-slate-500 truncate">{u.email}</p>}
                                            </div>
                                            <Badge className="bg-rose-100 text-rose-700 text-[9px] shrink-0">{u.abandono_label}</Badge>
                                        </div>
                                        {u.causa_posible && (
                                            <p className="text-[10px] text-slate-500 mt-0.5 italic">{u.causa_posible}</p>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// CONTAINER
// ─────────────────────────────────────────────────────────────────────────────
export default function ReportsPanel() {
    const [tab, setTab] = useState('daily');
    const [loading, setLoading] = useState(false);
    const [daily, setDaily] = useState(null);
    const [weekly, setWeekly] = useState(null);

    const fetchDaily = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/analytics/report-daily');
            setDaily(data);
        } catch {
            toast.error('No se pudo cargar el Informe Diario');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchWeekly = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/analytics/report-weekly');
            setWeekly(data);
        } catch {
            toast.error('No se pudo cargar el Informe Semanal');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (tab === 'daily' && !daily) fetchDaily();
        if (tab === 'weekly' && !weekly) fetchWeekly();
    }, [tab, daily, weekly, fetchDaily, fetchWeekly]);

    return (
        <Card data-testid="reports-panel" id="informes">
            <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <FileBarChart className="h-5 w-5" />
                    Informes de Gestión
                </CardTitle>
                <CardDescription>
                    Análisis automático de la actividad y conversión, con detección de puntos de fuga y alertas.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <Tabs value={tab} onValueChange={setTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-2 max-w-sm">
                        <TabsTrigger value="daily" data-testid="reports-tab-daily">Informe Diario</TabsTrigger>
                        <TabsTrigger value="weekly" data-testid="reports-tab-weekly">Informe Semanal</TabsTrigger>
                    </TabsList>
                    <TabsContent value="daily" className="mt-6">
                        {loading && !daily ? (
                            <div className="flex justify-center py-12">
                                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                            </div>
                        ) : (
                            <DailyReport data={daily} onRefresh={fetchDaily} loading={loading} />
                        )}
                    </TabsContent>
                    <TabsContent value="weekly" className="mt-6">
                        {loading && !weekly ? (
                            <div className="flex justify-center py-12">
                                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                            </div>
                        ) : (
                            <WeeklyReport data={weekly} onRefresh={fetchWeekly} loading={loading} />
                        )}
                    </TabsContent>
                </Tabs>
            </CardContent>
        </Card>
    );
}
